import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../utils/AuthContext';
import { EyeIcon, EyeSlashIcon, LockIcon, EnvelopeIcon, ArrowLeftIcon } from '../utils/IconWrapper';
import Button from '../components/ui/Button';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: var(--bg-primary);
  padding: 2rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 450px;
  width: 100%;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #004744;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 2rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 71, 68, 0.1);
    transform: translateX(-4px);
  }
`;

const LoginCard = styled(motion.div)`
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 2px solid var(--border-color);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--text-primary);
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #004744, #004744);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    color: var(--text-secondary);
    font-size: 1rem;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .icon {
    position: absolute;
    left: 1rem;
    color: var(--text-secondary);
    z-index: 1;
    transition: color 0.3s ease;
  }

  &:focus-within .icon {
    color: #004744;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #004744;
    box-shadow: 0 0 0 3px rgba(0, 71, 68, 0.1);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: #004744;
    background: rgba(0, 71, 68, 0.1);
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #ef4444;
  font-size: 0.9rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &::before {
    content: '⚠️';
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
`;

const Footer = styled.div`
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  
  p {
    color: var(--text-secondary);
    margin-bottom: 1rem;
  }
  
  a {
    color: #004744;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);

    const result = await login(email, password);
    
    setIsLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  return (
    <PageContainer>
      <Container>
        <BackLink to="/">
          <ArrowLeftIcon />
          Torna alla home
        </BackLink>

        <LoginCard
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Header>
            <h1>Benvenuto</h1>
            <p>Accedi al tuo account J-O-H</p>
          </Header>

          {/* Messaggio di verifica completata */}
          {searchParams.get('verified') === 'true' && (
            <div style={{
              background: 'rgba(0, 71, 68, 0.1)',
              border: '1px solid #004744',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <div style={{ color: '#004744', fontWeight: '600', marginBottom: '0.5rem' }}>
                ✅ Email verificata con successo!
              </div>
              <div style={{ color: '#004744', fontSize: '0.9rem' }}>
                Il tuo account è ora attivo. Accedi per iniziare a prenotare.
              </div>
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <InputWrapper>
                <EnvelopeIcon className="icon" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  required
                  autoComplete="email"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <InputWrapper>
                <LockIcon className="icon" size={18} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="La tua password"
                  required
                  autoComplete="current-password"
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
                </PasswordToggle>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </ErrorMessage>
            )}

            <LoginButton 
              type="submit" 
              variant="primary"
              disabled={isLoading || !email || !password}
            >
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </LoginButton>
          </Form>

          <Footer>
            <p>Non hai ancora un account?</p>
            <Link to="/register">Registrati ora</Link>
          </Footer>
        </LoginCard>
      </Container>
    </PageContainer>
  );
};

export default Login; 